import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { G, FONT_FAMILY } from "../constants/theme";

// ─── Email allowlist ──────────────────────────────────────────────────────────

const ALLOWED_DOMAIN = "ecosense-enviro.com";
const ALLOWED_EMAILS = new Set([
  "surekakrishika@gmail.com",
  "sahil2311@gmail.com",
  "vikramacharya009@gmail.com",
  "rishi.officl@gmail.com",
  "adu.subr@gmail.com",
]);

function isAllowedEmail(email) {
  const lower = email.toLowerCase().trim();
  return lower.endsWith(`@${ALLOWED_DOMAIN}`) || ALLOWED_EMAILS.has(lower);
}

// ─── Responsive hook ──────────────────────────────────────────────────────────

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

// ─── Feature icons ─────────────────────────────────────────────────────────────

function IconBarChart() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="9" width="3" height="6" rx="0.5" fill="currentColor" opacity="0.5" />
      <rect x="6.5" y="5" width="3" height="10" rx="0.5" fill="currentColor" />
      <rect x="12" y="1" width="3" height="14" rx="0.5" fill="currentColor" opacity="0.7" />
    </svg>
  );
}
function IconClock() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 4.5v3.5l2.5 1.5" />
    </svg>
  );
}
function IconCode() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="5 4 1 8 5 12" />
      <polyline points="11 4 15 8 11 12" />
    </svg>
  );
}
function IconTrend() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 12 5.5 7 9 10 15 3" />
      <polyline points="11 3 15 3 15 7" />
    </svg>
  );
}
function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="6.5" cy="6.5" r="5" />
      <line x1="10.5" y1="10.5" x2="14.5" y2="14.5" />
    </svg>
  );
}
function IconEye({ off = false }) {
  if (off) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M1.5 1.5l13 13" />
        <path d="M6.7 6.8A2 2 0 009.3 9.3" />
        <path d="M3.5 3.8C2.1 5 1.2 6.4 1 8c1.7 3.8 4 5.5 7 5.5 1.1 0 2.2-.3 3.2-.8" />
        <path d="M12.7 12.2C14 11 14.8 9.6 15 8c-1.7-3.8-4-5.5-7-5.5-.7 0-1.5.1-2.1.3" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M1 8C3 4 5.3 2.5 8 2.5S13 4 15 8c-2 4-4.3 5.5-7 5.5S3 12 1 8z" />
      <circle cx="8" cy="8" r="2" />
    </svg>
  );
}

// ─── Feature data ──────────────────────────────────────────────────────────────

const FEATURES = [
  { label: "Engineering Analytics", Icon: IconBarChart },
  { label: "Lifecycle Intelligence", Icon: IconClock },
  { label: "GitHub Insights",        Icon: IconCode },
  { label: "Delivery Metrics",       Icon: IconTrend },
  { label: "AI Investigation",       Icon: IconSearch },
];

// ─── Shared form primitives ───────────────────────────────────────────────────

function FormField({ label, id, type = "text", value, onChange, placeholder, disabled, autoComplete, error }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 20 }}>
      <label
        htmlFor={id}
        style={{ display: "block", fontSize: 13, fontWeight: 500, color: G.gray700, marginBottom: 6, fontFamily: FONT_FAMILY }}
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "11px 14px",
          fontSize: 14,
          fontFamily: FONT_FAMILY,
          color: G.gray900,
          background: disabled ? G.gray50 : G.white,
          border: `1px solid ${error ? G.red600 : focused ? G.brand : G.gray200}`,
          borderRadius: 6,
          outline: "none",
          boxShadow: focused && !error ? "0 0 0 3px rgba(37,99,235,0.08)" : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
          cursor: disabled ? "not-allowed" : "text",
        }}
      />
      {error && (
        <p style={{ margin: "5px 0 0", fontSize: 12, color: G.red600, fontFamily: FONT_FAMILY }}>{error}</p>
      )}
    </div>
  );
}

function PasswordField({ label, id, value, onChange, placeholder, disabled, autoComplete, error }) {
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  return (
    <div style={{ marginBottom: 20 }}>
      <label
        htmlFor={id}
        style={{ display: "block", fontSize: 13, fontWeight: 500, color: G.gray700, marginBottom: 6, fontFamily: FONT_FAMILY }}
      >
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "11px 44px 11px 14px",
            fontSize: 14,
            fontFamily: FONT_FAMILY,
            color: G.gray900,
            background: disabled ? G.gray50 : G.white,
            border: `1px solid ${error ? G.red600 : focused ? G.brand : G.gray200}`,
            borderRadius: 6,
            outline: "none",
            boxShadow: focused && !error ? "0 0 0 3px rgba(37,99,235,0.08)" : "none",
            transition: "border-color 0.15s, box-shadow 0.15s",
            cursor: disabled ? "not-allowed" : "text",
          }}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((v) => !v)}
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: G.gray400,
            display: "flex",
            alignItems: "center",
            lineHeight: 1,
          }}
        >
          <IconEye off={visible} />
        </button>
      </div>
      {error && (
        <p style={{ margin: "5px 0 0", fontSize: 12, color: G.red600, fontFamily: FONT_FAMILY }}>{error}</p>
      )}
    </div>
  );
}

function PrimaryButton({ label, loading, disabled }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="submit"
      disabled={loading || disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: "100%",
        padding: "11px 20px",
        fontSize: 14,
        fontWeight: 600,
        fontFamily: FONT_FAMILY,
        color: G.white,
        background: loading || disabled ? "rgba(37,99,235,0.5)" : hov ? G.brandHover : G.brand,
        border: "none",
        borderRadius: 6,
        cursor: loading || disabled ? "not-allowed" : "pointer",
        transition: "background 0.15s",
        letterSpacing: "0.01em",
      }}
    >
      {loading ? "Please wait…" : label}
    </button>
  );
}

function GoogleButton({ onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: "100%",
        padding: "10px 20px",
        fontSize: 14,
        fontWeight: 500,
        fontFamily: FONT_FAMILY,
        color: G.gray700,
        background: hov ? G.gray50 : G.white,
        border: `1px solid ${hov ? G.gray300 : G.gray200}`,
        borderRadius: 6,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        transition: "background 0.15s, border-color 0.15s",
      }}
    >
      {/* Google G mark — using existing brand palette */}
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: G.brand,
          color: G.white,
          fontSize: 11,
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontFamily: FONT_FAMILY,
          letterSpacing: 0,
        }}
      >
        G
      </div>
      Continue with Google
    </button>
  );
}

function Divider() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
      <div style={{ flex: 1, height: 1, background: G.gray200 }} />
      <span style={{ fontSize: 12, fontWeight: 500, color: G.gray400, fontFamily: FONT_FAMILY }}>
        or
      </span>
      <div style={{ flex: 1, height: 1, background: G.gray200 }} />
    </div>
  );
}

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div
      style={{
        marginBottom: 20,
        padding: "11px 14px",
        background: G.red50,
        border: `1px solid rgba(220,38,38,0.18)`,
        borderRadius: 6,
        fontSize: 13,
        color: G.red700,
        fontFamily: FONT_FAMILY,
        lineHeight: 1.5,
      }}
    >
      {message}
    </div>
  );
}

function SuccessBanner({ message }) {
  if (!message) return null;
  return (
    <div
      style={{
        marginBottom: 20,
        padding: "11px 14px",
        background: G.green50,
        border: `1px solid rgba(22,163,74,0.18)`,
        borderRadius: 6,
        fontSize: 13,
        color: G.green700,
        fontFamily: FONT_FAMILY,
        lineHeight: 1.5,
      }}
    >
      {message}
    </div>
  );
}

// ─── Left panel ───────────────────────────────────────────────────────────────

function LeftPanel({ compact = false }) {
  return (
    <div
      style={{
        width: "100%",
        background: G.dark,
        display: "flex",
        flexDirection: "column",
        padding: compact ? "40px 32px" : "48px 56px",
        boxSizing: "border-box",
        flexShrink: 0,
        minHeight: compact ? "auto" : "100vh",
      }}
    >
      {/* Top-left: logo + name */}
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 5,
            background: G.brand,
            color: G.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 19,
            fontWeight: 800,
            flexShrink: 0,
            fontFamily: FONT_FAMILY,
          }}
        >
          J
        </div>
        <span
          style={{
            fontSize: 19,
            fontWeight: 700,
            color: G.white,
            fontFamily: FONT_FAMILY,
            letterSpacing: "-0.01em",
          }}
        >
          JiraOps
        </span>
      </div>

      {/* Center content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: compact ? "flex-start" : "center",
          paddingTop: compact ? 40 : 0,
        }}
      >
        {/* Eyebrow */}
        <p
          style={{
            margin: "0 0 12px",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: G.brand,
            fontFamily: FONT_FAMILY,
          }}
        >
          Ecosense Enviro
        </p>

        {/* Heading */}
        <h1
          style={{
            margin: "0 0 18px",
            fontSize: compact ? 36 : 44,
            fontWeight: 700,
            color: G.white,
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
            fontFamily: FONT_FAMILY,
          }}
        >
          Engineering Intelligence
          <br />
          <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: 400 }}>
            Dashboard
          </span>
        </h1>

        {/* Tagline */}
        <p
          style={{
            margin: "0 0 40px",
            fontSize: 19,
            fontWeight: 400,
            color: "rgba(255,255,255,0.4)",
            lineHeight: 1.7,
            fontFamily: FONT_FAMILY,
            maxWidth: 340,
          }}
        >
          One consolidated view of sprint health, engineering delivery,
          developer productivity, and lifecycle intelligence.
        </p>

        {/* Feature grid — 2 columns */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "14px 24px",
          }}
        >
          {FEATURES.map(({ label, Icon }) => (
            <div
              key={label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span style={{ color: G.brand, flexShrink: 0, display: "flex", opacity: 0.9 }}>
                <Icon />
              </span>
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 500,
                  color: "rgba(255,255,255,0.55)",
                  fontFamily: FONT_FAMILY,
                  lineHeight: 1.3,
                }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom footer */}
      {!compact && (
        <p
          style={{
            margin: "48px 0 0",
            fontSize: 16,
            color: "rgba(255,255,255,0.18)",
            fontFamily: FONT_FAMILY,
          }}
        >
          Internal use only · Ecosense Enviro Solutions
        </p>
      )}
    </div>
  );
}

// ─── Sign-in form ──────────────────────────────────────────────────────────────

function SignInForm({ onSuccess, onSwitchToSignUp, onForgotPassword, successMessage }) {
  const { login } = useAuth();
  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [googleNote, setGoogleNote] = useState(false);

  const validate = () => {
    const errs = {};
    if (!email.trim()) errs.email = "Email is required.";
    else if (!email.includes("@")) errs.email = "Enter a valid email address.";
    if (!password) errs.password = "Password is required.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setFieldErrors({});
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password, rememberMe);
      onSuccess();
    } catch (err) {
      setError(err.message || "Sign in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <SuccessBanner message={successMessage} />
      <ErrorBanner message={error} />

      <FormField
        label="Work Email"
        id="signin-email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@ecosense-enviro.com"
        disabled={loading}
        autoComplete="email"
        error={fieldErrors.email}
      />

      <PasswordField
        label="Password"
        id="signin-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Enter your password"
        disabled={loading}
        autoComplete="current-password"
        error={fieldErrors.password}
      />

      {/* Remember me + Forgot */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
          marginTop: -4,
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            cursor: "pointer",
            fontSize: 13,
            color: G.gray600,
            fontFamily: FONT_FAMILY,
          }}
        >
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            disabled={loading}
            style={{ cursor: "pointer", accentColor: G.brand }}
          />
          Remember me
        </label>
        <button
          type="button"
          onClick={onForgotPassword}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            fontSize: 13,
            color: G.brand,
            cursor: "pointer",
            fontFamily: FONT_FAMILY,
            fontWeight: 500,
          }}
        >
          Forgot password?
        </button>
      </div>

      <PrimaryButton label="Sign In" loading={loading} />

      <Divider />

      <GoogleButton onClick={() => setGoogleNote(true)} />

      {googleNote && (
        <p
          style={{
            margin: "10px 0 0",
            fontSize: 12,
            color: G.gray500,
            textAlign: "center",
            fontFamily: FONT_FAMILY,
          }}
        >
          Google Sign-In will be available in a future update.
        </p>
      )}

      <p
        style={{
          textAlign: "center",
          marginTop: 24,
          fontSize: 13,
          color: G.gray500,
          fontFamily: FONT_FAMILY,
        }}
      >
        Don't have an account?{" "}
        <button
          type="button"
          onClick={onSwitchToSignUp}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: G.brand,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: FONT_FAMILY,
          }}
        >
          Create account
        </button>
      </p>
    </form>
  );
}

// ─── Sign-up form ──────────────────────────────────────────────────────────────

function SignUpForm({ onSuccess, onSwitchToSignIn }) {
  const { register } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!displayName.trim() || displayName.trim().length < 2)
      errs.displayName = "Display name must be at least 2 characters.";
    if (!email.trim()) errs.email = "Email is required.";
    else if (!isAllowedEmail(email))
      errs.email = "This email is not authorized for this application.";
    if (!password) errs.password = "Password is required.";
    else if (password.length < 8) errs.password = "Must be at least 8 characters.";
    if (password !== confirmPassword) errs.confirmPassword = "Passwords do not match.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setFieldErrors({});
    setError("");
    setLoading(true);
    try {
      await register(email.trim(), password, displayName.trim());
      onSuccess();
    } catch (err) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <ErrorBanner message={error} />

      <FormField
        label="Full Name"
        id="signup-name"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        placeholder="Jane Smith"
        disabled={loading}
        autoComplete="name"
        error={fieldErrors.displayName}
      />

      <FormField
        label="Work Email"
        id="signup-email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@ecosense-enviro.com"
        disabled={loading}
        autoComplete="email"
        error={fieldErrors.email}
      />

      <PasswordField
        label="Password"
        id="signup-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Minimum 8 characters"
        disabled={loading}
        autoComplete="new-password"
        error={fieldErrors.password}
      />

      <PasswordField
        label="Confirm Password"
        id="signup-confirm"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder="Repeat your password"
        disabled={loading}
        autoComplete="new-password"
        error={fieldErrors.confirmPassword}
      />

      <div style={{ marginTop: 4, marginBottom: 24 }} />

      <PrimaryButton label="Create Account" loading={loading} />

      <p
        style={{
          textAlign: "center",
          marginTop: 24,
          fontSize: 13,
          color: G.gray500,
          fontFamily: FONT_FAMILY,
        }}
      >
        Already have an account?{" "}
        <button
          type="button"
          onClick={onSwitchToSignIn}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: G.brand,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: FONT_FAMILY,
          }}
        >
          Sign in
        </button>
      </p>
    </form>
  );
}

// ─── Forgot-password form ──────────────────────────────────────────────────────

function ForgotPasswordForm({ onBack }) {
  const [email, setEmail]       = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [fieldError, setFieldError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) { setFieldError("Email is required."); return; }
    if (!isAllowedEmail(email)) {
      setFieldError("This email is not authorized for this application.");
      return;
    }
    setFieldError("");
    setSubmitted(true);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {submitted ? (
        <div
          style={{
            padding: "14px 16px",
            background: G.green50,
            border: `1px solid rgba(22,163,74,0.18)`,
            borderRadius: 6,
            marginBottom: 20,
          }}
        >
          <p style={{ margin: 0, fontSize: 13, color: G.green700, lineHeight: 1.6, fontFamily: FONT_FAMILY }}>
            Password resets are managed by your system administrator. Please
            contact your IT admin or email{" "}
            <strong>admin@ecosense-enviro.com</strong> for immediate access.
          </p>
        </div>
      ) : (
        <>
          <p
            style={{
              margin: "0 0 24px",
              fontSize: 14,
              color: G.gray500,
              lineHeight: 1.65,
              fontFamily: FONT_FAMILY,
            }}
          >
            Enter your work email address and we'll provide next steps for
            resetting your password.
          </p>

          <FormField
            label="Work Email"
            id="forgot-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@ecosense-enviro.com"
            autoComplete="email"
            error={fieldError}
          />

          <div style={{ marginTop: 4, marginBottom: 24 }} />
          <PrimaryButton label="Send Instructions" loading={false} />
        </>
      )}

      <p
        style={{
          textAlign: "center",
          marginTop: 24,
          fontSize: 13,
          color: G.gray500,
          fontFamily: FONT_FAMILY,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: G.brand,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: FONT_FAMILY,
          }}
        >
          ← Back to Sign In
        </button>
      </p>
    </form>
  );
}

// ─── Right panel / Auth section ───────────────────────────────────────────────

const VIEW_META = {
  signin: {
    heading: "Welcome Back",
    subtitle: "Sign in to continue to your dashboard.",
  },
  signup: {
    heading: "Create Account",
    subtitle: "Create your Ecosense Enviro account.",
  },
  forgot: {
    heading: "Reset Password",
    subtitle: "Enter your work email to continue.",
  },
};

function RightPanel({ isMobile = false }) {
  const navigate = useNavigate();
  const [view, setView]               = useState("signin");
  const [successMessage, setSuccessMessage] = useState("");

  const meta = VIEW_META[view];

  const TAB = (active) => ({
    flex: 1,
    padding: "11px 0",
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    color: active ? G.gray900 : G.gray400,
    background: "none",
    border: "none",
    borderBottom: `2px solid ${active ? G.brand : "transparent"}`,
    cursor: "pointer",
    fontFamily: FONT_FAMILY,
    transition: "color 0.15s, border-color 0.15s",
    marginBottom: -1,
  });

  return (
    <div
      style={{
        flex: 1,
        width: "100%",
        background: G.white,
        display: isMobile ? "block" : "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "48px 24px 40px" : "48px 40px",
        boxSizing: "border-box",
        minHeight: isMobile ? "auto" : "100vh",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          margin: "0 auto",
          minWidth: 0,
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          {/* Logo */}
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              background: G.brand,
              color: G.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              fontWeight: 800,
              fontFamily: FONT_FAMILY,
              margin: "0 auto 10px",
            }}
          >
            J
          </div>
          <p
            style={{
              margin: "0 0 28px",
              fontSize: 13,
              color: G.gray400,
              fontFamily: FONT_FAMILY,
              fontWeight: 500,
            }}
          >
            JiraOps
          </p>

          <h2
            style={{
              margin: "0 0 8px",
              fontSize: 26,
              fontWeight: 700,
              color: G.gray900,
              letterSpacing: "-0.02em",
              fontFamily: FONT_FAMILY,
            }}
          >
            {meta.heading}
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: G.gray500,
              fontFamily: FONT_FAMILY,
              lineHeight: 1.5,
            }}
          >
            {meta.subtitle}
          </p>
        </div>

        {/* Tabs */}
        {view !== "forgot" && (
          <div
            style={{
              display: "flex",
              borderBottom: `1px solid ${G.gray200}`,
              marginBottom: 28,
            }}
          >
            <button
              style={TAB(view === "signin")}
              onClick={() => { setView("signin"); setSuccessMessage(""); }}
            >
              Sign In
            </button>
            <button
              style={TAB(view === "signup")}
              onClick={() => { setView("signup"); setSuccessMessage(""); }}
            >
              Sign Up
            </button>
          </div>
        )}

        {/* Forms */}
        {view === "signin" && (
          <SignInForm
            onSuccess={() => navigate("/", { replace: true })}
            onSwitchToSignUp={() => { setView("signup"); setSuccessMessage(""); }}
            onForgotPassword={() => setView("forgot")}
            successMessage={successMessage}
          />
        )}
        {view === "signup" && (
          <SignUpForm
            onSuccess={() => {
              setSuccessMessage("Account created successfully. Please sign in.");
              setView("signin");
            }}
            onSwitchToSignIn={() => setView("signin")}
          />
        )}
        {view === "forgot" && (
          <ForgotPasswordForm onBack={() => setView("signin")} />
        )}
      </div>
    </div>
  );
}

// ─── Page root ────────────────────────────────────────────────────────────────

export default function Login() {
  const { user, loading } = useAuth();
  const navigate          = useNavigate();
  const width             = useWindowWidth();

  const isMobile = width < 640;
  const isTablet = width >= 640 && width < 1024;

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  // Mobile: form first, compact branding below
  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", fontFamily: FONT_FAMILY, overflowX: "hidden" }}>
        <RightPanel isMobile />
        <LeftPanel compact />
      </div>
    );
  }

  // Tablet: form only, no side panel (panel is too narrow at 768px to render cleanly)
  if (isTablet) {
    return (
      <div style={{ minHeight: "100vh", background: G.white, display: "flex", fontFamily: FONT_FAMILY }}>
        <RightPanel />
      </div>
    );
  }

  // Desktop: 45% dark left panel + 55% white right panel
  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        fontFamily: FONT_FAMILY,
      }}
    >
      <div style={{ width: "45%", flexShrink: 0, minHeight: "100vh" }}>
        <LeftPanel />
      </div>
      <RightPanel />
    </div>
  );
}
